import React, { useState } from "react";
import { api } from "../../../../api";
import toast from "react-hot-toast";
import { useCustomToast } from "../../../../components/customtoast/CustomToastProvider";
import { profile } from "../../../../api/profile";
import { useMutation } from "react-query";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate } from './../../../../utils/dateFormatter';
import { useTranslation } from 'react-i18next';

function AddExperience({ closeModal, refetch, refetchStatus }) {
  const { t } = useTranslation();
  const user = JSON.parse(sessionStorage.getItem('user'))?.user
  const customToast = useCustomToast();
  const [values, setValues] = useState({
    name: "",
    role: "",
    description: "",
    start_date: "",
    end_date: "",
  });

  const [isCurrentlyWorking, setIsCurrentlyWorking] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const { isLoading, mutateAsync } = useMutation(profile.createUserExperiencev2)

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });

    // Clear the validation error for the field when the user starts typing again
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleDateInputChange = (name, value) => {
    setValues({ ...values, [name]: value });

    // Clear the validation error for the field when the user starts typing again
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleCurrentlyWorkingChange = (e) => {
    setIsCurrentlyWorking(e.target.checked);
  };

  const createExperience = async (e) => {
    e.preventDefault();
  
    const requiredFields = ["name", "role", "description", "start_date"];
    const newValidationErrors = {};
  
    requiredFields.forEach((field) => {
      if (!values[field]) {
        newValidationErrors[field] = t('add-experience.validation.required');
      }
    });
  
    if (!values.start_date) {
      newValidationErrors.start_date = t('add-experience.validation.required');
    } else if (new Date(values.start_date) > new Date()) {
      newValidationErrors.start_date = t('add-experience.validation.start-date-future');
    }
  
    if (!isCurrentlyWorking && values.end_date) {
      if (!values.start_date || new Date(values.start_date) > new Date(values.end_date)) {
        newValidationErrors.start_date = t('add-experience.validation.date-order.start');
        newValidationErrors.end_date = t('add-experience.validation.date-order.end');
      }
    }
  
    if (Object.keys(newValidationErrors).length > 0) {
      setValidationErrors(newValidationErrors);
      return;
    }
  
    try {
      toast.loading(t('add-experience.loading'));
      let payload = {
        ...values, start_date: formatDate(values.start_date), 
        end_date: isCurrentlyWorking ? null : formatDate(values.end_date) 
      }
      const res = await mutateAsync({ id: user?.user_id, payload});
      toast.remove();
      customToast.success(res.message);
      sessionStorage.setItem("progress", "experience");
      closeModal(false);
      await refetch();
      await refetchStatus();
    } catch (error) {
      if (error.res) {
        toast.remove()
        customToast.error(error.res.data.message);
      } else {
        customToast.error(error.message);
      }
    }
  };

  return (
    <div className="fixed top-0 left-0 w-screen h-screen grid place-items-center bg-black bg-opacity-50 p-10 ">
      <div className="">
        <div className="min-h-[12rem] md:w-[600px] bg-white rounded-[32px] flex flex-col justify-center p-6 md:p-8 relative">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {t('add-experience.title')}
          </h3>
          <div className="flex justify-between mb-4">
            <div className="text-darkBlue text-md font-medium">
              {t('add-experience.subtitle')}
            </div>
          </div>
          <form onSubmit={createExperience}>
            {/* Company Name Field */}
            <div className="mb-4">
              <label htmlFor="name" className="text-gray-500 mb-4 text-base font-medium">
                {t('add-experience.company.label')}
                <span className="text-red-500 required-dot">*</span>
              </label>
              <input
                value={values.name}
                onChange={handleInputChange}
                type="text"
                id="name"
                className={`rounded-lg flex-1 appearance-none border ${
                  validationErrors.name ? "border-red-500" : "border-lightGrey"
                } w-full py-4 px-4 bg-white text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-lightGrey focus:border-transparent`}
                name="name"
                placeholder={t('add-experience.company.placeholder')}
              />
              {validationErrors.name && (
                <p className="text-red-500 text-md mt-1 text-right">
                  {validationErrors.name}
                </p>
              )}
            </div>

            {/* Role Field */}
            <div className="flex flex-col md:flex-row items-center gap-x-2 mb-4">
              <div className="w-full mb-4 md:mb-0">
                <div className="flex flex-col">
                  <label htmlFor="role" className="text-gray-500 mb-2 text-base font-medium">
                    {t('add-experience.role.label')}
                    <span className="text-red-500 required-dot">*</span>
                  </label>
                  <input
                    type="text"
                    id="role"
                    name="role"
                    placeholder={t('add-experience.role.placeholder')}
                    value={values.role}
                    onChange={handleInputChange}
                    className={`rounded-lg flex-1 appearance-none border ${
                      validationErrors.role ? "border-red-500" : "border-lightGrey"
                    } py-4 px-4 bg-white text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-lightGrey focus:border-transparent`}
                  />
                  {validationErrors.role && (
                    <p className="text-red-500 text-md mt-1 text-right">
                      {validationErrors.role}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Dates Section */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
              {/* Start Date */}
              <div className="mb-4 md:mb-0">
                <label htmlFor="start_date" className="text-gray-500 block mb-1 text-base font-medium">
                  {t('add-experience.dates.start.label')}
                  <span className="text-red-500 required-dot mr-2">*</span>
                </label>
                <DatePicker
                  selected={values.start_date}
                  placeholderText={t('add-experience.dates.start.placeholder')}
                  dateFormat="dd/MM/yyyy"
                  className="border pr-16 border-slate-300 block w-full focus:border-primary active:border-primary py-4 px-3 rounded-lg text-sm text-slate-700"
                  maxDate={values.end_date}
                  onChange={(value) => handleDateInputChange('start_date', value)}
                />
                {validationErrors.start_date && (
                  <p className="text-red-500 text-md mt-1 text-right">
                    {validationErrors.start_date}
                  </p>
                )}
              </div>

              {/* End Date */}
              <div className="mb-4 md:mb-0">
                <label htmlFor="end_date" className="text-gray-500 mb-1 block text-base font-medium">
                  {t('add-experience.dates.end.label')}
                </label>
                {!isCurrentlyWorking && (
                  <DatePicker
                    selected={values.end_date}
                    placeholderText={t('add-experience.dates.end.placeholder')}
                    dateFormat="dd/MM/yyyy"
                    className="border pr-16 border-slate-300 block w-full focus:border-primary active:border-primary py-4 px-3 rounded-lg text-sm text-slate-700"
                    minDate={values.start_date}
                    onChange={(value) => handleDateInputChange('end_date', value)}
                  />
                )}
                <div className="mt-3">
                  <label htmlFor="current-checkbox" className="flex items-center">
                    <input
                      type="checkbox"
                      id="current-checkbox"
                      checked={isCurrentlyWorking}
                      onChange={handleCurrentlyWorkingChange}
                    />
                    <span className="ml-4">{t('add-experience.dates.current')}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-4">
              <label htmlFor="description" className="text-gray-500 mb-2 text-base font-medium">
                {t('add-experience.description.label')}
                <span className="text-red-500 required-dot">*</span>
              </label>
              <textarea
                value={values.description}
                onChange={handleInputChange}
                className={`mt-4 flex-1 w-full px-4 py-2 text-base text-gray-700 placeholder-gray-400 bg-white border ${
                  validationErrors.description ? "border-red-500" : "border-lightGrey"
                } rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-lightGrey focus:border-transparent`}
                id="description"
                name="description"
                placeholder={t('add-experience.description.placeholder')}
                rows="5"
                cols="40"
              ></textarea>
              {validationErrors.description && (
                <p className="text-red-500 text-md mt-1 text-right">
                  {validationErrors.description}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-x-10 justify-center">
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-primary shadow-sm px-8 py-2 md:px-16 md:py-4 bg-white text-sm md:text-lg font-medium text-darkBlue sm:ml-3 sm:w-auto sm:text-sm mb-4 sm:mb-0"
                onClick={() => closeModal(false)}
              >
                {t('add-experience.buttons.cancel')}
              </button>

              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex justify-center rounded-md border border-none shadow-sm px-8 py-2 md:px-16 md:py-4 bg-primary text-sm md:text-lg font-medium text-white sm:ml-3 sm:w-auto sm:text-sm mb-4 sm:mb-0"
              >
                {t('add-experience.buttons.submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddExperience;
